"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight, MapPin, Phone } from "lucide-react";
export default function LocationHours() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const hours = [{
    day: "MON",
    time: "06:00 — 21:00"
  }, {
    day: "TUE",
    time: "06:00 — 21:00"
  }, {
    day: "WED",
    time: "06:00 — 21:00"
  }, {
    day: "THU",
    time: "06:00 — 21:00"
  }, {
    day: "FRI",
    time: "06:00 — 21:00"
  }, {
    day: "SAT",
    time: "08:00 — 14:00"
  }, {
    day: "SUN",
    time: "CLOSED"
  }];
  const address = "450 Fitness Way, Peak City, ST 90210";
  const phone = "(555) 247-KIRA";
  const email = "train@coachkirafit.com";
  const directionsUrl = "https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent(address);
  const mapEmbedUrl = "https://www.google.com/maps?q=" + encodeURIComponent(address) + "&output=embed";
  return <section data-section-id="108" id="location-hours" className="dark bg-background text-foreground py-24 md:py-[140px] px-4 sm:px-6 lg:px-12 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 flex flex-col transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            

            <h2 className="font-default text-6xl sm:text-7xl md:text-8xl font-bold leading-[0.95] tracking-tight mb-14">
              Start /
              <br />
              <span className="italic text-primary">Strong.</span>
            </h2>

            <div className="flex flex-col gap-10 mb-12">
              {}
              <div className="border-t border-border pt-6">
                <span className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase block mb-3">
                  Location
                </span>
                <p className="font-default text-lg leading-snug text-foreground">
                  450 Fitness Way
                  <br />
                  Peak City, ST 90210
                </p>
              </div>

              {}
              <div className="border-t border-border pt-6">
                <span className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase block mb-4">
                  Training Hours
                </span>
                <ul className="flex flex-col gap-2">
                  {hours.map((row, i) => <li key={i} data-index={i} className="flex items-center justify-between font-mono text-sm text-foreground">
                      <span className="text-muted-foreground">{row.day}</span>
                      <span className="border-b border-dashed border-border flex-1 mx-3 mb-1" />
                      <span>{row.time}</span>
                    </li>)}
                </ul>
              </div>

              {}
              <div className="border-t border-border pt-6">
                <span className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase block mb-3">
                  Connect
                </span>
                <p className="font-default text-lg leading-snug text-foreground">
                  {phone}
                  <br />
                  <span className="text-muted-foreground">{email}</span>
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild variant="outline" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border shadow-xs hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 py-2 has-[>svg]:px-3 group h-12 px-6 border-primary bg-primary hover:bg-primary/90 font-mono text-xs tracking-widest uppercase text-primary">
                <Link to={directionsUrl} newTab>
                  Get Directions
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="group h-12 px-6 border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground font-mono text-xs tracking-widest uppercase">
                <Link to={`tel:${phone.replace(/[^0-9+]/g, "")}`}>
                  Call Coach
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative w-full h-[500px] md:h-[640px] border border-border overflow-hidden">
              {}
              <iframe src={mapEmbedUrl} title="Studio location map" loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="absolute inset-0 w-full h-full grayscale contrast-125 brightness-[0.4] invert hue-rotate-180" style={{
              border: 0
            }} />

              {}
              <div className="absolute inset-0 bg-background/30 pointer-events-none mix-blend-multiply" />

              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                <div className="relative flex items-center justify-center">
                  <span className="absolute inline-flex h-12 w-12 bg-primary opacity-30 animate-ping" />
                  <span className="absolute inline-flex h-8 w-8 bg-primary opacity-50 animate-pulse" />
                  <span className="relative inline-flex items-center justify-center h-6 w-6 bg-primary shadow-lg">
                    <MapPin className="size-3.5 text-primary-foreground" />
                  </span>
                </div>
              </div>

              {}
              <div className="absolute top-5 left-5 md:top-6 md:left-6 max-w-[280px] bg-card border border-border p-5 shadow-xl">
                <div className="flex items-center gap-2 mb-3">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full bg-primary opacity-75" />
                    <span className="relative inline-flex h-2 w-2 bg-primary" />
                  </span>
                  <span className="font-mono text-[10px] tracking-widest text-foreground uppercase">
                    Training Now
                  </span>
                </div>
                <p className="font-default text-lg leading-tight text-card-foreground mb-1">
                  Coach Kira Fit
                </p>
                <p className="font-mono text-xs text-muted-foreground leading-relaxed">
                  450 Fitness Way
                  <br />
                  Peak City, ST 90210
                </p>
              </div>

              {}
              <div className="absolute bottom-5 right-5 md:hidden">
                <Button asChild className="h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-[10px] tracking-widest uppercase">
                  <Link to={directionsUrl} newTab>
                    Open in Maps
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>

              {}
              <div className="hidden md:flex absolute bottom-5 left-5 items-center gap-2 font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
                <Phone className="size-3" />
                <span>{phone}</span>
              </div>
              <div className="hidden md:block absolute top-5 right-5 font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
                34.0522° N / 118.2437° W
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}